import React, { useState, useEffect } from "react";
import { Button, Select, Spin, message } from "antd";
import { createContractor, updateException, getAllContractors } from "../api";
import { ExceptionStatus } from "../ExceptionDrawer";
import Hide from "../../components/Hide";

const { Option } = Select;

const ContractorComponent = ({ exception, onClose, onResolved }) => {
    const [contractorName, setContractorName] = useState(null);
    const [showSelect, setShowSelect] = useState(false);
    const [constructors, setConstructors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedAliasId, setSelectedAliasId] = useState(null);

    useEffect(() => {
        const extractContractorName = (message) => {
            setContractorName(exception?.unknown_infomation);
        };

        if (exception && exception.description) {
            extractContractorName(exception.description);
        }

        const fetchContractors = async () => {
            setLoading(true);
            try {
                const allContractors = (await getAllContractors()).data;
                setConstructors(allContractors); 
            } catch (error) {
                console.error("Error fetching contractors", error);
            } finally {
                setLoading(false);
            }
        };

        fetchContractors();
    }, [exception]);

    const handleMarkAsNew = async () => {
        if (contractorName) {
            if (exception.status === 'pending') {
                try {
                    setLoading(true);
                    await createContractor({ name: contractorName });
                    await updateException(exception.id);
                    console.log(`Contractor "${contractorName}" created successfully.`);
                    message.success('Contractor created');
                } catch (error) {
                    console.error(`Failed to create contractor "${contractorName}".`, error);
                    message.error('Failed to create contractor');
                } finally {
                    setLoading(false);
                    try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                    onClose();
                }
            } else {
                console.warn("Exception already resolved.");
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            }
        } else {
            console.warn("No contractor name found.");
            onClose();
        }
    };

    const handleMarkAsAliasFor = () => {
        setShowSelect(true);
    };

    const handleContractorSelect = (value) => {
        setSelectedAliasId(value ?? null);
    };

    const handleSaveAlias = async () => {
        if (!selectedAliasId) return;
        if (exception.status === 'pending') {
            try {
                setLoading(true);
                await createContractor({ name: contractorName, alias_for: selectedAliasId });
                await updateException(exception.id);
                console.log(`Contractor "${contractorName}" set as alias for contractor with ID: ${selectedAliasId}`);
                message.success('Alias saved');
            } catch (error) {
                console.error(`Failed to update alias for contractor with ID: ${selectedAliasId}`, error);
                message.error('Failed to save alias');
            } finally {
                setLoading(false);
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            }
        } else {
            console.warn("Exception already resolved.");
            onClose();
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button 
                            type="primary" 
                            onClick={handleMarkAsNew}
                            disabled={loading}
                            loading={loading}
                        >
                            Mark as New
                        </Button>
                        <Button 
                            type="default" 
                            onClick={handleMarkAsAliasFor}
                            disabled={loading}
                        >
                            Mark as Alias For
                        </Button>
                        {showSelect && (
                            <>
                                <div style={{ fontSize: 12, color: '#666' }}>
                                    {contractorName ? `Select an existing contractor to alias "${contractorName}" to.` : 'Select an existing contractor to alias.'}
                                </div>
                                <Select
                                    style={{ minWidth: 220 }}
                                    placeholder="Select a contractor"
                                    onChange={handleContractorSelect}
                                    disabled={loading}
                                    value={selectedAliasId ?? undefined}
                                >
                                    {constructors?.map((contractor) => (
                                        <Option key={contractor.id} value={contractor.id}>
                                            {contractor.name}
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    type="primary"
                                    onClick={handleSaveAlias}
                                    disabled={loading || !selectedAliasId}
                                    loading={loading}
                                >
                                    Save
                                </Button>
                            </>
                        )}
                    </div>
                </Spin>
            </Hide>
        </div>
    );
};

export default ContractorComponent;
